import BaseballPhase from "app/components/BaseballPhase/BaseballPhase";
import BasketballPhase from "app/components/BasketballPhase/BasketballPhase";
import BoxingPhase from "app/components/BoxingPhase/BoxingPhase";
import IceHockeyPhase from "app/components/IceHockeyPhase/IceHockeyPhase";
import { MomentTime } from "app/components/Moment/Moment";
import EventPhase from "app/screens/EventsScreen/components/EventPhase/EventPhase";
import type { EventIncidentType, EventType } from "app/types/eventType";
import {
  ScreenType,
  SportsNameType,
  SUPPORTEDLANGUAGES,
} from "app/utils/const";
import type { Node } from "React";
import React from "react";
import { LanguageSelectionHelper } from "./languageSelectionHelper";

export class EventTimeHelper {
  static lang = LanguageSelectionHelper.checkCookieLanguageSelected();
  // There are different layouts for time details for certain sports
  // Baseball has a unique display showing Innings instead of time
  // The default for the other views is the original display used for Soccer
  // or it just simply returns "remainingTime" value if it's available
  static getEventTimeComponent: (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean,
    isEventInExtraTime: boolean | null,
    viewType: string
  ) => Node = (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean,
    isEventInExtraTime: boolean | null,
    viewType: string
  ) => {
    const selectedLanguage = SUPPORTEDLANGUAGES.find(
      language => language.locale === EventTimeHelper.lang
    );

    // when language is set to spanish and the sport is baseball we dont display the event info
    // in the events cell, this is due to the spanish translation being too long and breaking the UI
    // instead we prevent it from showing in the events cell but show it instead when the user
    // clicks into the event, the data is still viewable there.
    if (
      selectedLanguage?.locale === "es-ES" &&
      eventData.sportType === SportsNameType.BASEBALL
    ) {
      // Language is es-ES and sportType is BASEBALL, don't render BaseballPhase
      return null;
    } else if (eventData.sportType === SportsNameType.BASEBALL) {
      return <BaseballPhase eventData={eventData} />;
      // return <BaseballPhase eventData={eventData} />;
    } else if (eventData.sportType === SportsNameType.BASKETBALL) {
      // event time quarters update for basketball
      return <BasketballPhase eventData={eventData} />;
    } else if (eventData.sportType === SportsNameType.BOXING) {
      return <BoxingPhase eventData={eventData} />;
    } else if (eventData.sportType === SportsNameType.ICE_HOCKEY) {
      // event time phases update for ice hockey
      return <IceHockeyPhase eventData={eventData} />;
    } else if (viewType === ScreenType.ASIAN) {
      return this.renderAsianEventCellTime(
        eventData,
        eventIncidents,
        isBeforeExtra
      );
    } else if (viewType === ScreenType.EUROPEAN) {
      return this.renderEuroEventCellTime(
        eventData,
        eventIncidents,
        isBeforeExtra,
        isEventInExtraTime
      );
    }
  };

  static renderAsianEventCellTime: (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean
  ) => Node = (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean
  ) => {
    return (
      <React.Fragment>
        {eventIncidents[0]["remainingTime"] ? (
          <div className="eventPhase">{eventIncidents[0]["remainingTime"]}</div>
        ) : (
          <React.Fragment>
            <EventPhase eventIncident={eventIncidents[0]} />
            {isBeforeExtra ? (
              ""
            ) : (
              <MomentTime
                value={eventIncidents[0]["phaseStartDate"]}
                eventPhase={eventIncidents[0]["eventPhase"]}
                countDown
              />
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  static renderEuroEventCellTime: (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean,
    isEventInExtraTime: boolean | null
  ) => Node = (
    eventData: EventType,
    eventIncidents: EventIncidentType[],
    isBeforeExtra: boolean,
    isEventInExtraTime: boolean | null
  ) => {
    return (
      <React.Fragment>
        {eventIncidents[0]["remainingTime"] ? (
          <div className="eventPhase">{eventIncidents[0]["remainingTime"]}</div>
        ) : (
          <React.Fragment>
            <EventPhase eventIncident={eventIncidents[0]} />
            {isBeforeExtra ? (
              ""
            ) : (
              <MomentTime
                value={eventIncidents[0]["phaseStartDate"]}
                eventPhase={eventIncidents[0]["eventPhase"]}
                countDown
              />
            )}
            {isEventInExtraTime ? (
              <span className="extra-time-et">&nbsp;ET</span>
            ) : (
              ""
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  static setOrdinalNumber: (n: number) => string = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
}
